import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { X } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import { globalErrorHandler } from './utils/errorHandler';
import AppTheme from './theme/AppTheme';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import AccountsPage from './pages/portal/wallet/AccountsPage.tsx';
import ProfilePage from './pages/portal/profile/ProfilePage';
import AuthLayout from './layouts/AuthLayout';
import TradingLayout from './layouts/TradingLayout';
import CRMLayout from './layouts/CRMLayout';

// Import existing CRM pages
import CRMDashboardPage from './pages/crm/DashboardPage';
import CRMUsersPage from './pages/crm/UsersPage';
import UserCreatePage from './pages/crm/UserCreatePage';
import UserEditPage from './pages/crm/UserEditPage';
import KycManagementPage from './pages/crm/KycManagementPage';
import KycDetailsPage from './pages/crm/KycDetailsPage';
import AuditLogPage from './pages/crm/AuditLogPage';
import CRMLeadsPage from './pages/crm/LeadsPage';
import LeadDetailsPage from './pages/crm/Leads/LeadDetailsPage';
import CampaignsPage from './pages/crm/CampaignsPage';
import CampaignCreatePage from './pages/crm/Campaigns/CampaignCreatePage';
import CampaignDetailsPage from './pages/crm/Campaigns/CampaignDetailsPage';
import CampaignEditPage from './pages/crm/Campaigns/CampaignEditPage';
import SystemConfigurationPage from './pages/crm/SystemConfigurationPage';
import InstrumentsPage from './pages/crm/InstrumentsPage';
import CRMCustomersPage from './pages/crm/Customers/CustomersPage.tsx';
import CustomerDetailsPage from './pages/crm/Customers/CustomerDetailsPage.tsx';
import CustomerCreatePage from './pages/crm/Customers/CustomerCreatePage.tsx';
import RolesManagement from './pages/RolesManagement';
import RoleDetailsPage from './pages/RoleDetailsPage';
import RoleEditPage from './pages/RoleEditPage';
import NotificationCenter from './pages/crm/NotificationCenter';
import PaymentMethodsPage from './pages/crm/PaymentMethodsPage';
import PaymentMethodProcessorConfig from './pages/admin/PaymentMethodProcessorConfig';
import TradesPage from './pages/crm/TradesPage.tsx';
import TradingAccountsPage from './pages/crm/TradingAccountsPage';
import TransactionsPage from './pages/crm/TransactionsPage';
import TradingPage from './pages/portal/trade/TradingPage.tsx';
import { AdminLayout } from '@/layouts/AdminLayout.tsx';
import { TenantManagementPage } from '@/pages/admin/TenantManagementPage.tsx';
import { TenantDetailsPage } from '@/pages/admin/TenantDetailsPage.tsx';
import { TenantProvider } from '@/contexts/TenantContext.tsx';
import { IntegrationProvider } from '@/contexts/IntegrationContext';
import { IntegrationsPage } from '@/pages/admin/IntegrationsPage.tsx';
import ViewsManagementPage from './pages/crm/ViewsManagementPage';
// Mobile pages
import MobileHomePage from './pages/mobile/MobileHomePage';
import MobileAccountsPage from './pages/mobile/MobileAccountsPage';
import MobileMarketPage from './pages/mobile/MobileMarketPage';
import MobileTradingPage from './pages/mobile/MobileTradingPage';
import MobileDepositWithdrawPage from './pages/mobile/MobileDepositWithdrawPage';
import MobileProfilePage from './pages/mobile/MobileProfilePage';
import MobileHistoryPage from './pages/mobile/MobileHistoryPage';
import ResponsiveRoute from './components/ResponsiveRoute';
import { FaviconSetter } from './components/FaviconSetter';
import { PageTitleSetter } from './components/PageTitleSetter';
import { TenantThemeSetter } from './components/TenantThemeSetter';
import { PageTitleProvider } from './contexts/PageTitleContext';
import { ConfirmProvider } from './hooks/useConfirm';
import { PromptProvider } from './hooks/usePromptDialog';

const App: React.FC = () => {
    const { isAdmin, isCRM, isTrader } = useAuth();

    // Fix mobile zoom issue on first load
    useEffect(() => {
        // Reset zoom for mobile devices, especially iOS Safari
        const resetZoom = () => {
            const viewport = document.querySelector('meta[name="viewport"]');
            if (viewport && /iPhone|iPad|iPod|Android/.test(navigator.userAgent)) {
                // Force reset viewport to prevent zoom
                viewport.setAttribute(
                    'content',
                    'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes, shrink-to-fit=no'
                );
            }
        };

        // Reset immediately and after a short delay
        resetZoom();
        const timeout = setTimeout(resetZoom, 100);

        // Also reset on orientation change
        window.addEventListener('orientationchange', resetZoom);

        return () => {
            clearTimeout(timeout);
            window.removeEventListener('orientationchange', resetZoom);
        };
    }, []);

    return (
        <ErrorBoundary onError={error => globalErrorHandler.handleError(error, 'react-boundary')}>
            <AppTheme>
                <PageTitleProvider>
                    <FaviconSetter />
                    <PageTitleSetter />
                    <TenantThemeSetter />
                    <div className="min-h-screen bg-background text-foreground">
                        <TenantProvider>
                            <IntegrationProvider>
                                <SnackbarProvider
                                    maxSnack={3}
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    dense={false}
                                    preventDuplicate={true}
                                    autoHideDuration={2800}
                                    action={snackbarKey => (
                                        <button
                                            type="button"
                                            onClick={() => closeSnackbar(snackbarKey)}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium text-inherit opacity-80 hover:opacity-100"
                                            aria-label="Dismiss notification"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                >
                                    <ConfirmProvider>
                                        <PromptProvider>
                                            <Routes>
                                                {/* Public Routes */}
                                                <Route element={<AuthLayout />}>
                                                    <Route
                                                        path="/auth/login"
                                                        element={<LoginPage />}
                                                    />
                                                    <Route
                                                        path="/auth/register"
                                                        element={<RegisterPage />}
                                                    />
                                                    <Route
                                                        path="/auth/forgot-password"
                                                        element={<ForgotPasswordPage />}
                                                    />
                                                    <Route
                                                        path="/auth/reset-password"
                                                        element={<ResetPasswordPage />}
                                                    />
                                                    <Route
                                                        path="/auth/verify-email"
                                                        element={<VerifyEmailPage />}
                                                    />
                                                </Route>

                                                {/* Protected Trader Routes - Same routes for mobile and desktop */}
                                                {isTrader() && (
                                                    <Route path="/" element={<TradingLayout />}>
                                                        {/* Home - Desktop shows AccountsPage, Mobile shows MobileHomePage */}
                                                        <Route
                                                            index
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={<MobileHomePage />}
                                                                    desktop={<AccountsPage />}
                                                                />
                                                            }
                                                        />

                                                        {/* Accounts - Desktop shows AccountsPage, Mobile shows MobileAccountsPage */}
                                                        <Route
                                                            path="accounts"
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={<MobileAccountsPage />}
                                                                    desktop={<AccountsPage />}
                                                                />
                                                            }
                                                        />

                                                        {/* Market page - Mobile only, redirect on desktop */}
                                                        <Route
                                                            path="market"
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={<MobileMarketPage />}
                                                                    desktop={
                                                                        <Navigate to="/" replace />
                                                                    }
                                                                />
                                                            }
                                                        />

                                                        {/* Trading page */}
                                                        <Route
                                                            path="trade"
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={<MobileTradingPage />}
                                                                    desktop={<TradingPage />}
                                                                />
                                                            }
                                                        />
                                                        <Route
                                                            path="trade/:instrumentId"
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={<MobileTradingPage />}
                                                                    desktop={<TradingPage />}
                                                                />
                                                            }
                                                        />

                                                        {/* Deposit/Withdraw - Mobile only, redirect on desktop */}
                                                        <Route
                                                            path="deposit-withdraw"
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={
                                                                        <MobileDepositWithdrawPage />
                                                                    }
                                                                    desktop={
                                                                        <Navigate to="/" replace />
                                                                    }
                                                                />
                                                            }
                                                        />

                                                        {/* Profile */}
                                                        <Route
                                                            path="profile"
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={<MobileProfilePage />}
                                                                    desktop={<ProfilePage />}
                                                                />
                                                            }
                                                        />

                                                        {/* History - Mobile only, redirect on desktop */}
                                                        <Route
                                                            path="history"
                                                            element={
                                                                <ResponsiveRoute
                                                                    mobile={<MobileHistoryPage />}
                                                                    desktop={
                                                                        <Navigate to="/" replace />
                                                                    }
                                                                />
                                                            }
                                                        />
                                                    </Route>
                                                )}

                                                {/* CRM Routes */}
                                                {isCRM() && (
                                                    <Route path="/" element={<CRMLayout />}>
                                                        {/* Dashboard Routes */}
                                                        <Route
                                                            index
                                                            element={<CRMDashboardPage />}
                                                        />

                                                        {/* Communication & Marketing Routes */}
                                                        <Route
                                                            path="notification-center"
                                                            element={<NotificationCenter />}
                                                        />

                                                        {/* Core CRM Routes */}
                                                        <Route
                                                            path="customers"
                                                            element={<CRMCustomersPage />}
                                                        />
                                                        <Route
                                                            path="customers/create"
                                                            element={<CustomerCreatePage />}
                                                        />
                                                        <Route
                                                            path="customers/:customerId"
                                                            element={<CustomerDetailsPage />}
                                                        />
                                                        <Route
                                                            path="leads"
                                                            element={<CRMLeadsPage />}
                                                        />
                                                        <Route
                                                            path="leads/:leadId"
                                                            element={<LeadDetailsPage />}
                                                        />
                                                        <Route
                                                            path="campaigns"
                                                            element={<CampaignsPage />}
                                                        />
                                                        <Route
                                                            path="campaigns/new"
                                                            element={<CampaignCreatePage />}
                                                        />
                                                        <Route
                                                            path="campaigns/:campaignId"
                                                            element={<CampaignDetailsPage />}
                                                        />
                                                        <Route
                                                            path="campaigns/:campaignId/edit"
                                                            element={<CampaignEditPage />}
                                                        />
                                                        <Route
                                                            path="kyc-requests"
                                                            element={<KycManagementPage />}
                                                        />
                                                        <Route
                                                            path="kyc-requests/:id"
                                                            element={<KycDetailsPage />}
                                                        />
                                                        <Route
                                                            path="users"
                                                            element={<CRMUsersPage />}
                                                        />
                                                        <Route
                                                            path="users/create"
                                                            element={<UserCreatePage />}
                                                        />
                                                        <Route
                                                            path="users/:id/edit"
                                                            element={<UserEditPage />}
                                                        />
                                                        <Route
                                                            path="roles"
                                                            element={<RolesManagement />}
                                                        />
                                                        <Route
                                                            path="roles/create"
                                                            element={<RoleEditPage />}
                                                        />
                                                        <Route
                                                            path="roles/:id"
                                                            element={<RoleDetailsPage />}
                                                        />
                                                        <Route
                                                            path="roles/:id/edit"
                                                            element={<RoleEditPage />}
                                                        />
                                                        <Route
                                                            path="payment-methods"
                                                            element={<PaymentMethodsPage />}
                                                        />
                                                        <Route
                                                            path="payment-methods/:id/processors"
                                                            element={
                                                                <PaymentMethodProcessorConfig />
                                                            }
                                                        />
                                                        <Route
                                                            path="audit-logs"
                                                            element={<AuditLogPage />}
                                                        />
                                                        <Route
                                                            path="trades"
                                                            element={<TradesPage />}
                                                        />
                                                        <Route
                                                            path="trading-accounts"
                                                            element={<TradingAccountsPage />}
                                                        />
                                                        <Route
                                                            path="transactions"
                                                            element={<TransactionsPage />}
                                                        />
                                                        <Route
                                                            path="system-configuration"
                                                            element={<SystemConfigurationPage />}
                                                        />
                                                        <Route
                                                            path="views"
                                                            element={<ViewsManagementPage />}
                                                        />
                                                        <Route
                                                            path="instruments"
                                                            element={<InstrumentsPage />}
                                                        />
                                                        <Route
                                                            path="integrations"
                                                            element={<IntegrationsPage />}
                                                        />
                                                    </Route>
                                                )}

                                                {/* Admin Routes */}
                                                {isAdmin() && (
                                                    <Route path="/" element={<AdminLayout />}>
                                                        <Route
                                                            path="/"
                                                            element={
                                                                <Navigate to="/tenants" replace />
                                                            }
                                                        />
                                                        <Route
                                                            path="/tenants"
                                                            element={<TenantManagementPage />}
                                                        />
                                                        <Route
                                                            path="/tenants/:id"
                                                            element={<TenantDetailsPage />}
                                                        />
                                                    </Route>
                                                )}

                                                {/* Fallback Route */}
                                                <Route
                                                    path="*"
                                                    element={<Navigate to="/" replace />}
                                                />
                                            </Routes>
                                        </PromptProvider>
                                    </ConfirmProvider>
                                </SnackbarProvider>
                            </IntegrationProvider>
                        </TenantProvider>
                    </div>
                </PageTitleProvider>
            </AppTheme>
        </ErrorBoundary>
    );
};

export default App;
